import { useEffect, useState } from "react"
import { Badge, Box, Checkbox, HStack, Spinner, Text, VStack } from "@chakra-ui/react"
import { useMutation, useQuery } from "@apollo/client"
import {
  GetGlobalAlertTemplatesDocument,
  GrantTemplateVisibilityDocument,
  RevokeTemplateVisibilityDocument,
} from "@hydrologiq/gql-experience/graphql"
import Dialog from "@hydrologiq/shared/chakra/Dialog"
import { toaster } from "@hydrologiq/shared/chakra/Toaster"
import { UpdateButton } from "@hydrologiq/shared/content/components/Button"

interface Organization {
  id: string
  name: string
  slug: string
  enabled: boolean
  tenants: { id: string; name: string; enabled: boolean }[]
  seatLimit?: number | null
  memberCount: number
  createdAt: string
}

interface ManageAlertsDialogProps {
  open: boolean
  organization: Organization
  onClose: () => void
}

interface TemplateVisibility {
  tenantId: string
  tenantName: string
  grantedAt: string | null
}

interface AlertTemplate {
  id: string
  name: string
  description: string | null
  severity: string
  metric: string | null
  scopeLevel: string | null
  visibleToTenants: TemplateVisibility[]
}

export function ManageAlertsDialog({ open, organization, onClose }: ManageAlertsDialogProps) {
  const { data, loading, refetch } = useQuery(GetGlobalAlertTemplatesDocument, {
    fetchPolicy: "cache-and-network",
  })

  const [grantVisibility] = useMutation(GrantTemplateVisibilityDocument)
  const [revokeVisibility] = useMutation(RevokeTemplateVisibilityDocument)

  const [selectedTemplateIds, setSelectedTemplateIds] = useState<Set<string>>(new Set())
  const [submitting, setSubmitting] = useState(false)

  // The org's tenant is used under the hood for visibility
  const orgTenantId = organization.tenants?.[0]?.id

  const templates: AlertTemplate[] = (data?.globalAlertTemplates ?? []) as AlertTemplate[]

  // Initialise selection based on which templates already have visibility for this org's tenant
  useEffect(() => {
    if (!orgTenantId || !templates.length) return
    const granted = new Set(
      templates
        .filter(t => t.visibleToTenants.some(v => v.tenantId === orgTenantId))
        .map(t => t.id)
    )
    setSelectedTemplateIds(granted)
  }, [templates, orgTenantId])

  const handleToggle = (templateId: string) => {
    setSelectedTemplateIds(prev => {
      const next = new Set(prev)
      if (next.has(templateId)) {
        next.delete(templateId)
      } else {
        next.add(templateId)
      }
      return next
    })
  }

  const handleApply = async () => {
    if (!orgTenantId) {
      toaster.error({ title: "Organisation has no linked tenant" })
      return
    }

    setSubmitting(true)
    try {
      const currentlyGranted = new Set(
        templates
          .filter(t => t.visibleToTenants.some(v => v.tenantId === orgTenantId))
          .map(t => t.id)
      )

      const toGrant = [...selectedTemplateIds].filter(id => !currentlyGranted.has(id))
      const toRevoke = [...currentlyGranted].filter(id => !selectedTemplateIds.has(id))

      if (toGrant.length > 0) {
        for (const templateId of toGrant) {
          await grantVisibility({ variables: { templateId, tenantIds: [orgTenantId] } })
        }
      }
      if (toRevoke.length > 0) {
        for (const templateId of toRevoke) {
          await revokeVisibility({ variables: { templateId, tenantIds: [orgTenantId] } })
        }
      }

      await refetch()
      toaster.success({ title: "Alert assignments updated" })
      onClose()
    } catch (error: any) {
      toaster.error({ title: "Failed to update alerts", description: error.message })
    } finally {
      setSubmitting(false)
    }
  }

  if (!orgTenantId) {
    return (
      <Dialog open={open} setOpen={(v) => { if (!v) onClose() }} title={`Manage Alerts - ${organization.name}`}>
        <Text color="fg.muted">This organisation has no linked tenant. Alerts cannot be assigned.</Text>
      </Dialog>
    )
  }

  return (
    <Dialog open={open} setOpen={(v) => { if (!v) onClose() }} title={`Manage Alerts - ${organization.name}`}>
      <VStack gap={3} align="stretch">
        <Text fontSize="sm" color="fg.muted">
          Select which alerts should be available to this organisation.
        </Text>

        {loading && !templates.length ? (
          <Box display="flex" justifyContent="center" py={4}>
            <Spinner size="sm" />
          </Box>
        ) : templates.length === 0 ? (
          <Text fontSize="sm" color="fg.muted">No alerts available.</Text>
        ) : (
          <VStack gap={2} align="stretch" maxH="400px" overflowY="auto">
            {templates.map(template => (
              <HStack
                key={template.id}
                gap={3}
                p={2}
                borderRadius="md"
                _hover={{ bg: "bg.subtle" }}
              >
                <Checkbox.Root
                  checked={selectedTemplateIds.has(template.id)}
                  onCheckedChange={() => handleToggle(template.id)}
                >
                  <Checkbox.HiddenInput />
                  <Checkbox.Control />
                  <Checkbox.Label>
                    <VStack gap={0} align="start">
                      <HStack gap={2}>
                        <Text fontSize="sm" fontWeight="medium">{template.name}</Text>
                        <Badge size="sm" colorPalette={
                          template.severity === "critical" ? "red" :
                          template.severity === "warning" ? "orange" : "blue"
                        }>
                          {template.severity}
                        </Badge>
                      </HStack>
                      {template.description && (
                        <Text fontSize="xs" color="fg.muted">{template.description}</Text>
                      )}
                    </VStack>
                  </Checkbox.Label>
                </Checkbox.Root>
              </HStack>
            ))}
          </VStack>
        )}

        <HStack justify="flex-end" pt={2}>
          <UpdateButton
            context={{ label: "Apply", description: "Save alert assignments for this organisation" }}
            onClick={handleApply}
            loading={submitting}
          />
        </HStack>
      </VStack>
    </Dialog>
  )
}
